/**
 * ConnectFourAI - AI opponent implementation with minimax algorithm
 *
 * Implements an AI Connect Four player with configurable difficulty levels
 * (easy, medium, hard). Uses minimax with alpha-beta pruning for move
 * calculation and various evaluation functions to assess board positions.
 *
 * Requirements: 8.1, 8.2, 8.3, 9.1, 9.2, 9.3, 9.4, 10.1, 10.2, 10.3, 10.4, 10.5
 */

import {
  CellState,
  ConnectFourGrid,
  ConnectFourDifficulty,
  DiscColor,
  ROWS,
  COLS,
} from './types';
import { ConnectFourEngine } from './ConnectFourEngine';

/**
 * Evaluation weights per difficulty level.
 *
 * | Factor                    | Easy | Medium | Hard |
 * |---------------------------|------|--------|------|
 * | Center column control     | —    | 30%    | 20%  |
 * | Connection threats        | —    | 30%    | 25%  |
 * | Blocking opponent         | —    | 25%    | 20%  |
 * | Two-way traps             | —    | —      | 15%  |
 * | Vertical stacking         | —    | —      | 10%  |
 * | Opponent threat assess.   | —    | 15%    | 10%  |
 * | Search depth              | 0    | 4      | 8    |
 * | Move strategy             | rand | minimax| mm+αβ|
 * | Time limit                | 500ms| 1s     | 2s   |
 */
interface EvaluationWeights {
  centerControl: number;
  connectionThreats: number;
  blocking: number;
  twoWayTraps: number;
  verticalStacking: number;
  opponentThreat: number;
}

const WEIGHTS: Record<ConnectFourDifficulty, EvaluationWeights> = {
  easy: {
    centerControl: 0,
    connectionThreats: 0,
    blocking: 0,
    twoWayTraps: 0,
    verticalStacking: 0,
    opponentThreat: 0,
  },
  medium: {
    centerControl: 0.30,
    connectionThreats: 0.30,
    blocking: 0.25,
    twoWayTraps: 0,
    verticalStacking: 0,
    opponentThreat: 0.15,
  },
  hard: {
    centerControl: 0.20,
    connectionThreats: 0.25,
    blocking: 0.20,
    twoWayTraps: 0.15,
    verticalStacking: 0.10,
    opponentThreat: 0.10,
  },
};

/** Score constants */
const WIN_SCORE = 100000;
const LOSE_SCORE = -100000;

export class ConnectFourAI {
  private difficulty: ConnectFourDifficulty;

  constructor(difficulty: ConnectFourDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  // ==========================================================================
  // Difficulty Configuration
  // ==========================================================================

  getDifficulty(): ConnectFourDifficulty {
    return this.difficulty;
  }

  setDifficulty(difficulty: ConnectFourDifficulty): void {
    this.difficulty = difficulty;
  }

  /**
   * Get the minimax search depth for the current difficulty.
   * Easy: 0 (random), Medium: 4, Hard: 8
   */
  getSearchDepth(): number {
    switch (this.difficulty) {
      case 'easy': return 0;
      case 'medium': return 4;
      case 'hard': return 8;
    }
  }

  /**
   * Determine if the AI should make a random move.
   * Easy: always random (100%), Medium/Hard: never
   */
  shouldMakeRandomMove(): boolean {
    return this.difficulty === 'easy';
  }

  // ==========================================================================
  // Main AI Method
  // ==========================================================================

  /**
   * Calculate the best move for the current position.
   *
   * - Easy: random valid column, ≤500ms
   * - Medium: minimax depth 4, ≤1s
   * - Hard: minimax depth 8 with alpha-beta pruning, ≤2s
   *
   * Returns the column index (0-6) or null if no valid moves.
   */
  calculateBestMove(engine: ConnectFourEngine, timeLimit: number): number | null {
    const validColumns = engine.getValidColumns();
    if (validColumns.length === 0) return null;

    // Easy: random valid column
    if (this.shouldMakeRandomMove()) {
      return validColumns[Math.floor(Math.random() * validColumns.length)];
    }

    const depth = this.getSearchDepth();
    const startTime = Date.now();
    const deadline = startTime + timeLimit;
    const currentPlayer = engine.getCurrentPlayer();
    const opponent: DiscColor = currentPlayer === 'red' ? 'yellow' : 'red';

    // PRIORITY 1: Check for immediate win
    for (const col of validColumns) {
      const cloned = this.cloneEngine(engine);
      const result = cloned.dropDisc(col);
      if (result.success && result.gameStatus.type === 'win') {
        return col; // Take the win immediately
      }
    }

    // PRIORITY 2: Block opponent's immediate win
    for (const col of validColumns) {
      // Simulate opponent playing this column
      const cloned = this.cloneEngine(engine);
      // Temporarily switch to opponent
      const state = cloned.getGameState();
      const savedPlayer = state.currentPlayer;
      state.currentPlayer = opponent;
      cloned.setGameState(state);
      const result = cloned.dropDisc(col);
      if (result.success && result.gameStatus.type === 'win') {
        return col; // Block the opponent's winning move
      }
    }

    let bestCol = validColumns[0];
    let bestScore = -Infinity;

    // Order columns: center first for better pruning
    const orderedColumns = this.orderColumns(validColumns);

    for (const col of orderedColumns) {
      if (Date.now() >= deadline) break;

      // Clone engine and make the move
      const cloned = this.cloneEngine(engine);
      const result = cloned.dropDisc(col);
      if (!result.success) continue;

      // Evaluate with minimax
      const score = this.minimax(
        cloned,
        depth - 1,
        -Infinity,
        Infinity,
        false, // opponent's turn (minimizing)
        currentPlayer,
        deadline
      );

      if (score > bestScore) {
        bestScore = score;
        bestCol = col;
      }
    }

    return bestCol;
  }

  // ==========================================================================
  // Minimax with Alpha-Beta Pruning
  // ==========================================================================

  private minimax(
    engine: ConnectFourEngine,
    depth: number,
    alpha: number,
    beta: number,
    maximizing: boolean,
    aiPlayer: DiscColor,
    deadline: number
  ): number {
    // Base cases
    if (depth <= 0 || Date.now() >= deadline) {
      return this.evaluatePosition(engine.getBoard(), aiPlayer);
    }

    const status = engine.getGameStatus();
    if (status.type === 'win') {
      return status.winner === aiPlayer
        ? WIN_SCORE + depth
        : LOSE_SCORE - depth;
    }
    if (status.type === 'draw') {
      return 0;
    }

    const validColumns = engine.getValidColumns();
    if (validColumns.length === 0) return 0;

    const orderedColumns = this.orderColumns(validColumns);

    if (maximizing) {
      let maxScore = -Infinity;
      for (const col of orderedColumns) {
        if (Date.now() >= deadline) break;

        const cloned = this.cloneEngine(engine);
        const result = cloned.dropDisc(col);
        if (!result.success) continue;

        const score = this.minimax(cloned, depth - 1, alpha, beta, false, aiPlayer, deadline);
        maxScore = Math.max(maxScore, score);
        alpha = Math.max(alpha, score);
        if (beta <= alpha) break; // Beta cutoff
      }
      return maxScore;
    } else {
      let minScore = Infinity;
      for (const col of orderedColumns) {
        if (Date.now() >= deadline) break;

        const cloned = this.cloneEngine(engine);
        const result = cloned.dropDisc(col);
        if (!result.success) continue;

        const score = this.minimax(cloned, depth - 1, alpha, beta, true, aiPlayer, deadline);
        minScore = Math.min(minScore, score);
        beta = Math.min(beta, score);
        if (beta <= alpha) break; // Alpha cutoff
      }
      return minScore;
    }
  }

  // ==========================================================================
  // Evaluation Functions
  // ==========================================================================

  /**
   * Evaluate the board position from the perspective of the given player.
   * Positive score favors the player, negative favors the opponent.
   */
  evaluatePosition(grid: ConnectFourGrid, player: DiscColor): number {
    const weights = WEIGHTS[this.difficulty];
    const opponent: DiscColor = player === 'red' ? 'yellow' : 'red';

    let score = 0;

    // Center column control
    score += this.evaluateCenterControl(grid, player) * weights.centerControl * 100;

    // Connection threats (3-in-a-row with open space)
    score += this.evaluateThreats(grid, player) * weights.connectionThreats * 100;

    // Blocking opponent connections
    score -= this.evaluateThreats(grid, opponent) * weights.blocking * 100;

    // Two-way traps (double threats)
    score += this.evaluateTwoWayTraps(grid, player) * weights.twoWayTraps * 100;

    // Vertical stacking
    score += this.evaluateVerticalStacking(grid, player) * weights.verticalStacking * 100;

    // Opponent threat assessment
    score -= this.evaluateThreats(grid, opponent) * weights.opponentThreat * 100;

    // Evaluate all windows of 4
    score += this.evaluateAllWindows(grid, player);

    return score;
  }

  /**
   * Evaluate a window of 4 cells.
   * Returns a score based on the contents.
   */
  evaluateWindow(window: (CellState)[], player: DiscColor): number {
    const opponent: DiscColor = player === 'red' ? 'yellow' : 'red';

    const playerCount = window.filter(c => c === player).length;
    const opponentCount = window.filter(c => c === opponent).length;
    const emptyCount = window.filter(c => c === null).length;

    // Winning position
    if (playerCount === 4) return 1000;

    // Losing position — must block!
    if (opponentCount === 4) return -1000;

    // Strong threat: 3 in a row with 1 empty
    if (playerCount === 3 && emptyCount === 1) return 50;

    // CRITICAL: opponent 3 in a row — must block or lose next turn
    if (opponentCount === 3 && emptyCount === 1) return -80;

    // Building: 2 in a row with 2 empty
    if (playerCount === 2 && emptyCount === 2) return 5;

    // Opponent building
    if (opponentCount === 2 && emptyCount === 2) return -3;

    return 0;
  }

  // ==========================================================================
  // Evaluation Helpers
  // ==========================================================================

  /**
   * Evaluate center column control.
   * Pieces in the center column (col 3) are more valuable.
   */
  private evaluateCenterControl(grid: ConnectFourGrid, player: DiscColor): number {
    let score = 0;
    const centerCol = Math.floor(COLS / 2);

    for (let row = 0; row < ROWS; row++) {
      if (grid[row][centerCol] === player) {
        score += 3;
      }
      // Adjacent center columns also valuable
      if (centerCol - 1 >= 0 && grid[row][centerCol - 1] === player) score += 1;
      if (centerCol + 1 < COLS && grid[row][centerCol + 1] === player) score += 1;
    }

    return score;
  }

  /**
   * Evaluate connection threats (3-in-a-row with one empty).
   */
  private evaluateThreats(grid: ConnectFourGrid, player: DiscColor): number {
    let threats = 0;

    // Check all windows of 4
    // Horizontal
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col <= COLS - 4; col++) {
        const window = [grid[row][col], grid[row][col + 1], grid[row][col + 2], grid[row][col + 3]];
        const playerCount = window.filter(c => c === player).length;
        const emptyCount = window.filter(c => c === null).length;
        if (playerCount === 3 && emptyCount === 1) threats++;
      }
    }

    // Vertical
    for (let col = 0; col < COLS; col++) {
      for (let row = 0; row <= ROWS - 4; row++) {
        const window = [grid[row][col], grid[row + 1][col], grid[row + 2][col], grid[row + 3][col]];
        const playerCount = window.filter(c => c === player).length;
        const emptyCount = window.filter(c => c === null).length;
        if (playerCount === 3 && emptyCount === 1) threats++;
      }
    }

    // Diagonal ascending
    for (let row = 0; row <= ROWS - 4; row++) {
      for (let col = 0; col <= COLS - 4; col++) {
        const window = [grid[row][col], grid[row + 1][col + 1], grid[row + 2][col + 2], grid[row + 3][col + 3]];
        const playerCount = window.filter(c => c === player).length;
        const emptyCount = window.filter(c => c === null).length;
        if (playerCount === 3 && emptyCount === 1) threats++;
      }
    }

    // Diagonal descending
    for (let row = 3; row < ROWS; row++) {
      for (let col = 0; col <= COLS - 4; col++) {
        const window = [grid[row][col], grid[row - 1][col + 1], grid[row - 2][col + 2], grid[row - 3][col + 3]];
        const playerCount = window.filter(c => c === player).length;
        const emptyCount = window.filter(c => c === null).length;
        if (playerCount === 3 && emptyCount === 1) threats++;
      }
    }

    return threats;
  }

  /**
   * Evaluate two-way traps (positions where player has two simultaneous threats).
   */
  private evaluateTwoWayTraps(grid: ConnectFourGrid, player: DiscColor): number {
    let traps = 0;

    // For each empty cell, check if filling it would create multiple threats
    for (let col = 0; col < COLS; col++) {
      for (let row = 0; row < ROWS; row++) {
        if (grid[row][col] !== null) continue;

        // Temporarily place a disc
        grid[row][col] = player;
        const threatCount = this.countThreatsAt(grid, row, col, player);
        grid[row][col] = null;

        if (threatCount >= 2) traps++;
      }
    }

    return traps;
  }

  /**
   * Count threats created by a disc at a specific position.
   */
  private countThreatsAt(grid: ConnectFourGrid, row: number, col: number, player: DiscColor): number {
    let threats = 0;
    const directions = [
      { dr: 0, dc: 1 },  // horizontal
      { dr: 1, dc: 0 },  // vertical
      { dr: 1, dc: 1 },  // diagonal ascending
      { dr: 1, dc: -1 }, // diagonal descending
    ];

    for (const { dr, dc } of directions) {
      let count = 1;

      // Count in positive direction
      let r = row + dr;
      let c = col + dc;
      while (r >= 0 && r < ROWS && c >= 0 && c < COLS && grid[r][c] === player) {
        count++;
        r += dr;
        c += dc;
      }

      // Count in negative direction
      r = row - dr;
      c = col - dc;
      while (r >= 0 && r < ROWS && c >= 0 && c < COLS && grid[r][c] === player) {
        count++;
        r -= dr;
        c -= dc;
      }

      if (count >= 4) threats++;
    }

    return threats;
  }

  /**
   * Evaluate vertical stacking potential.
   * Reward building vertical threats.
   */
  private evaluateVerticalStacking(grid: ConnectFourGrid, player: DiscColor): number {
    let score = 0;

    for (let col = 0; col < COLS; col++) {
      let consecutive = 0;
      for (let row = 0; row < ROWS; row++) {
        if (grid[row][col] === player) {
          consecutive++;
          if (consecutive >= 2) score += consecutive;
        } else {
          consecutive = 0;
        }
      }
    }

    return score;
  }

  /**
   * Evaluate all windows of 4 cells on the board.
   */
  private evaluateAllWindows(grid: ConnectFourGrid, player: DiscColor): number {
    let score = 0;

    // Horizontal windows
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col <= COLS - 4; col++) {
        const window: CellState[] = [
          grid[row][col], grid[row][col + 1],
          grid[row][col + 2], grid[row][col + 3],
        ];
        score += this.evaluateWindow(window, player);
      }
    }

    // Vertical windows
    for (let col = 0; col < COLS; col++) {
      for (let row = 0; row <= ROWS - 4; row++) {
        const window: CellState[] = [
          grid[row][col], grid[row + 1][col],
          grid[row + 2][col], grid[row + 3][col],
        ];
        score += this.evaluateWindow(window, player);
      }
    }

    // Diagonal ascending windows
    for (let row = 0; row <= ROWS - 4; row++) {
      for (let col = 0; col <= COLS - 4; col++) {
        const window: CellState[] = [
          grid[row][col], grid[row + 1][col + 1],
          grid[row + 2][col + 2], grid[row + 3][col + 3],
        ];
        score += this.evaluateWindow(window, player);
      }
    }

    // Diagonal descending windows
    for (let row = 3; row < ROWS; row++) {
      for (let col = 0; col <= COLS - 4; col++) {
        const window: CellState[] = [
          grid[row][col], grid[row - 1][col + 1],
          grid[row - 2][col + 2], grid[row - 3][col + 3],
        ];
        score += this.evaluateWindow(window, player);
      }
    }

    return score;
  }

  // ==========================================================================
  // Helpers
  // ==========================================================================

  /**
   * Order columns for better alpha-beta pruning.
   * Center columns first, then outward.
   */
  private orderColumns(columns: number[]): number[] {
    const center = Math.floor(COLS / 2);
    return [...columns].sort((a, b) => Math.abs(a - center) - Math.abs(b - center));
  }

  /**
   * Clone a ConnectFourEngine by serializing and deserializing its state.
   */
  private cloneEngine(engine: ConnectFourEngine): ConnectFourEngine {
    const cloned = new ConnectFourEngine();
    const state = engine.getGameState();
    const deepCopy = JSON.parse(JSON.stringify(state));
    cloned.setGameState(deepCopy);
    return cloned;
  }
}
